import { storageService } from '@/core/services'

export interface Timezone {
  isAuto: ComputedRef<boolean>
  options: ComputedRef<TimezoneOption[]>
  resetTimezone: () => void
  restoreTimezone: () => void
  setTimezone: (value: string) => void
  timezone: ComputedRef<string>
  title: ComputedRef<string>
}

export interface TimezoneOption {
  title: string
  value: string
}

export function useTimezone(): Timezone {
  const isManualTimezone = useState('timezone-manual', () => false)
  const rootStore = useBrickRootStore()

  const timezone = computed(() => getActiveTimezone())
  const title = computed(() => getTimezoneTitle(timezone.value))
  const isAuto = computed(() => !isManualTimezone.value)

  const options = computed<TimezoneOption[]>(() => {
    const now = new Date()
    const current = timezone.value
    const rest = [...new Set([getSystemTimezone(), ...getTimezoneList()])]
      .filter((value) => value !== current)
      .map((value) => ({ offset: getTimezoneOffsetMinutes(value, now), value }))
      .sort((a, b) => a.offset - b.offset || a.value.localeCompare(b.value))
      .map(({ value }) => value)

    return [current, ...rest].map((value) => ({ title: getTimezoneTitle(value, now), value }))
  })

  function restoreTimezone(): void {
    const stored = storageService.get('timezone')

    isManualTimezone.value = isValidTimezone(stored)
    setActiveTimezone(
      isManualTimezone.value ? (stored ?? undefined) : getAutoTimezone(rootStore.config?.current_country),
    )
  }

  function setTimezone(value: string): void {
    if (!isValidTimezone(value)) {
      return
    }

    isManualTimezone.value = true
    setActiveTimezone(value)
    storageService.set('timezone', value)
  }

  function resetTimezone(): void {
    isManualTimezone.value = false
    setActiveTimezone(getAutoTimezone(rootStore.config?.current_country))
    storageService.remove('timezone')
  }

  return {
    isAuto,
    options,
    resetTimezone,
    restoreTimezone,
    setTimezone,
    timezone,
    title,
  }
}

export function useTimezoneClock(): ComputedRef<string> {
  const { timezone } = useTimezone()
  const { locale } = useLocale()

  const now = shallowRef(new Date())
  let timer: ReturnType<typeof setTimeout> | undefined

  function tick(): void {
    now.value = new Date()
    timer = setTimeout(tick, 60_000 - (now.value.getTime() % 60_000))
  }

  onMounted(tick)

  onBeforeUnmount(() => {
    clearTimeout(timer)
  })

  return computed(() => formatTimezoneClock(now.value, timezone.value, locale.value))
}
